import { CORES_SUGERIDAS, HEX } from '@/shared/lib/cores';
import { CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Input } from '@/shared/ui/input';
import { cn } from '@/shared/lib/cn';

/**
 * Cor de destaque: amostras + seletor nativo + campo hex.
 *
 * ⚠️ **Os três controles são necessários, não redundantes.**
 *
 * `<input type="color">` não tem nome acessível útil nem operação por teclado
 * em vários navegadores — abre um diálogo nativo do sistema que o leitor de
 * tela não descreve. O campo de texto com o hex é o caminho acessível de
 * verdade; as amostras são o caminho rápido.
 *
 * Movido de `features/documentos` pra cá quando `features/analises` passou a
 * precisar do mesmo controle (cor do grupo de estudo) — deixou de ser uso
 * único, que era o motivo original de não virar primitivo compartilhado.
 */
export function SeletorDeCor({ cor, aoMudar }: { cor: string; aoMudar: (cor: string) => void }) {
  const valido = HEX.test(cor);

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-brand">Cor de destaque</p>

      <div className="flex flex-wrap gap-2">
        {CORES_SUGERIDAS.map(({ hex, nome }) => (
          <button
            key={hex}
            type="button"
            aria-label={`Cor ${nome}`}
            aria-pressed={cor.toLowerCase() === hex}
            onClick={() => aoMudar(hex)}
            style={{ background: hex }}
            className={cn(
              'h-8 w-8 rounded-full border-2 transition-all',
              cor.toLowerCase() === hex
                ? 'border-foreground ring-2 ring-brand ring-offset-2 ring-offset-background'
                : 'border-border hover:scale-110',
            )}
          />
        ))}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label="Escolher outra cor"
          value={valido ? cor : '#691241'}
          onChange={(e) => aoMudar(e.target.value)}
          className="h-9 w-14 shrink-0 cursor-pointer rounded-md border border-input bg-background p-1"
        />
        <Input
          aria-label="Cor em hexadecimal"
          value={cor}
          maxLength={7}
          placeholder="#691241"
          onChange={(e) => aoMudar(e.target.value)}
          className={cn(CLASSE_CONTROLE, 'font-mono sm:max-w-[10rem]')}
        />
        {!valido && (
          <span className="text-xs text-destructive">Use o formato #RRGGBB.</span>
        )}
      </div>
    </div>
  );
}
